import React, { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import Form from '../../../../../components/Form'
import Field from '../../../../../components/Form/Field'
import AdAccountsSection from './AdAccountsSection'
import FieldLabelWrapper from '../../../../../features/components/Form/FieldLabelWrapper'
import Icon from '../../../../../components/Icon'
import Toggle from '../../../../../components/Toggle'

import { getAppDomainName } from '../../../../../features/helpers/getAppDomainName'

import { ReactComponent as CoinIcon } from '../../../../../assets/icons/coin-icon.svg'

import { formatOptionsList } from '../../../../../features/formatters'

import { clearSelfAccounts } from '../../../../../modules/actions/selfAccounts'
import { clearMembersList } from '../../../../../modules/actions/team'
import { selfAccountsSelector } from '../../../../../modules/selectors/selfAccounts'
import { membersSelector } from '../../../../../modules/selectors/team'

import { NAME, PARTNER, UTM_MEDIUM, AUTH_USER, SPEND_METRICS, DESCRIPTION } from './fields'

import useStyles from './styles'
import useDrawerFormClasses from '../../../../../styles/common/drawerForms'

const PartnershipForm = ({ formik, showPartnerSelector = true, isEditForm, ...formProps }) => {
  const classes = useStyles()
  const drawerFormClasses = useDrawerFormClasses()

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const members = useSelector(membersSelector)
  const selfAccounts = useSelector(selfAccountsSelector)
  const appName = getAppDomainName()

  const { values, setFieldValue } = formik

  const formattedSelfAccounts = useMemo(
    () =>
      selfAccounts.map(account => ({
        value: account.id,
        label: account.name
      })),
    [selfAccounts]
  )

  const formattedMembers = useMemo(
    () =>
      formatOptionsList({
        list: members,
        labelName: 'full_name',
        valueName: 'user'
      }),
    [members]
  )

  const handlerSpendMetricsCheck = useCallback(() => {
    setFieldValue(SPEND_METRICS, !values[SPEND_METRICS])
  }, [setFieldValue, values])

  useEffect(
    () => () => {
      dispatch(clearSelfAccounts())
      dispatch(clearMembersList())
    },
    [dispatch]
  )

  return (
    <Form formik={formik} {...formProps}>
      <section className={drawerFormClasses.section}>
        <h3 className={drawerFormClasses.sectionTitle}>
          {showPartnerSelector ? 'Partner and partnership name' : 'Partnership name'}
        </h3>
        <p>
          The partnership name should be a descriptive name that signifies an agreed media partnership with a partner.
        </p>
        {showPartnerSelector && (
          <FieldLabelWrapper label={t('Partner')}>
            <Field
              placeholder="Select partner..."
              formik={formik}
              name={PARTNER}
              options={formattedSelfAccounts}
              enableReinitialize
            />
          </FieldLabelWrapper>
        )}
        <FieldLabelWrapper label={t('Partnership name')} labelFor={NAME}>
          <Field
            placeholder="Name of partnership"
            disablePlaceholderFormatting
            formik={formik}
            name={NAME}
            enableReinitialize
            autoComplete="off"
          />
        </FieldLabelWrapper>

        <FieldLabelWrapper label={t('Partnership description')} labelFor={DESCRIPTION}>
          <Field
            placeholder="Description"
            formik={formik}
            name={DESCRIPTION}
            isTextarea
            enableReinitialize
            autoComplete="off"
          />
        </FieldLabelWrapper>
      </section>

      <section className={drawerFormClasses.section}>
        <h3 className={drawerFormClasses.sectionTitle}>Ad accounts</h3>
        <p>
          Please enter the ad account details for each platform that will be made available for campaign creation for
          the partner under this partnership. You are not required to add all platforms.
        </p>
        <AdAccountsSection formik={formik} showAcceptTosLink={isEditForm} />
      </section>

      <section className={drawerFormClasses.section}>
        <h3 className={drawerFormClasses.sectionTitle}>Authorisation user</h3>
        <p>
          Partners don’t normally have direct access to the ad accounts and can only view and manage campaigns via
          {appName}. Therefore, please indicate which user to authorise with when making requests by a partner.
        </p>
        <p>
          If you leave this blank, if a partner tries to view or manage a campaign, they will be prompted to authorise
          with the ad platform and will require user access to the ad account.
        </p>
        <Field placeholder="Auth User" formik={formik} name={AUTH_USER} options={formattedMembers} enableReinitialize />
      </section>

      <section className={drawerFormClasses.section}>
        <h3 className={drawerFormClasses.sectionTitle}>utm_medium override</h3>
        <p>
          As part of the ad creation process {appName} suggests UTM codes to add to a click through link so that the
          partner can easily view post-click actions in their own analytics platform.
        </p>
        <p>
          As default, we suggest <b>utm_medium={appName.toLowerCase()}</b> but if you would like to override that to
          something else for this partnership, please enter the text here.
        </p>
        <Field
          placeholder="Utm_Medium"
          disablePlaceholderFormatting
          formik={formik}
          name={UTM_MEDIUM}
          enableReinitialize
        />
      </section>

      <section className={drawerFormClasses.section}>
        <h3 className={drawerFormClasses.sectionTitle}>Spend metrics</h3>
        <p>
          Campaign reports include spend metrics (CPM, CPC, total spend etc.). If you would like to hide all metrics
          related to spend from the partner, please toggle spend metrics off. This might be done for partners who are
          receiving a fixed number of impressions as part of a wider package.
        </p>
        <p>
          When off, the partner will only see performance metrics not related to spend such as impressions, clicks, CTR,
          engagements, video views and conversions.
        </p>
        <div className={classes.spendMetricsBlock}>
          <Icon className={classes.coinIcon}>
            <CoinIcon />
          </Icon>
          <div>Spend Metrics</div>
          <Toggle isSolid={false} isEnabled={values[SPEND_METRICS]} onToggle={handlerSpendMetricsCheck} />
        </div>
      </section>
    </Form>
  )
}

export default PartnershipForm
